import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'

import FeatureToggle from '~/constants/featureToggle'
import InvoiceType from '~/constants/InvoiceType'
import { convertToLineItems } from '~/store/actions/finance'
import {
  getFeatureToggle,
  getIsSetBundlePriceEnabled,
} from '~/store/reducers/constants'
import type { Estimate, InvoiceLineItem, Order } from '~/types'
import { convertOrderToInvoiceLineItem, isBundle } from '~/utils/orderUtils'

interface UseEstimateCheckAllItemsProps {
  clientId: string
  estimate: Estimate
  patientId: string
}

export const useEstimateCheckAllItems = ({
  estimate,
  clientId,
  patientId,
}: UseEstimateCheckAllItemsProps) => {
  const dispatch = useDispatch()
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const isSetBundlePriceEnabled = useSelector(getIsSetBundlePriceEnabled)

  return useCallback(
    (order: Order, checked: boolean) => {
      if (!isBundle(order) || !order.items || !checked) return

      const lineItems = order.items.map((item) => ({
        ...convertOrderToInvoiceLineItem(item, isFoodCatalogEnabled),
        group: item.bundleId || item.group,
        ...(isSetBundlePriceEnabled && {
          fixedPrice: order.fixedPrice ?? undefined,
        }),
      })) as InvoiceLineItem[]

      // This order contains additionalDiscount from Bundle object
      const additionalDiscount = R.prop('additionalDiscount', order) ?? 0

      dispatch(
        convertToLineItems({
          invoiceId: estimate.id,
          clientId,
          patientId,
          invoiceType: InvoiceType.ESTIMATE.toUpperCase(),
          otcItems: lineItems,
          bundleDiscount: additionalDiscount,
        }),
      )
    },
    [
      estimate.id,
      clientId,
      patientId,
      dispatch,
      isFoodCatalogEnabled,
      isSetBundlePriceEnabled,
    ],
  )
}
